import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { getMaterialDisplayLabel } from '@/utils/materialSystem';
import { useUnifiedAuth } from '@/hooks/useUnifiedAuth';
import { useAppSelector } from '@/store/hooks';
import type { FilterState } from './useFilters';

interface EvolutionDataPoint {
  period: string;
  [materialType: string]: number | string;
}

export type GranularityType = 'day' | 'week' | 'month';
export type MetricType = 'units' | 'weight' | 'volume';

export const useEvolutionChartData = (
  filters?: FilterState,
  granularity: GranularityType = 'day',
  metric: MetricType = 'weight'
) => {
  const { profile } = useUnifiedAuth();
  const rawMaterials = useAppSelector((s) => s.establishmentAccess.materials);
  const materialNames = useMemo(
    () =>
      (filters?.materials?.map((id) => rawMaterials.find((m) => m.id === id)?.name).filter(Boolean) ?? []) as string[],
    [filters?.materials, rawMaterials]
  );

  // Validação de parâmetros - garante que seja um boolean
  const isValid = !!(filters?.dateRange?.start && filters?.dateRange?.end);
  
  const isInternal = profile?.is_internal === true;
  const isAdmin = profile?.permission_role === 'admin';
  const isStandard = profile?.permission_role === 'standard';

  const queryKey = [
    'evolution-chart-data-v5', // Added STANDARD user permission filtering
    {
      startDate: filters?.dateRange?.start?.toISOString().split('T')[0] || null,
      endDate: filters?.dateRange?.end?.toISOString().split('T')[0] || null,
      materials: filters?.materials || [],
      states: filters?.states || [],
      establishments: filters?.establishments || [],
      generators: filters?.generators || [],
      userGeneratorId: profile?.generator_id_legacy || null,
      userId: profile?.user_id || null,
      isInternal: isInternal,
      permissionRole: profile?.permission_role || null,
    },
    granularity,
    metric
  ];

  return useQuery({
    queryKey,
    queryFn: async () => {
      // Validação antes de fazer a chamada
      if (!isValid || !filters?.dateRange?.start || !filters?.dateRange?.end) {
        return [];
      }

      if (!profile) {
        return [];
      }
      
      try {
        // Build RPC parameters
        const rpcParams: any = {
          start_date: filters.dateRange.start.toISOString().split('T')[0],
          end_date: filters.dateRange.end.toISOString().split('T')[0],
          materials: materialNames.length ? materialNames : null,
          states: filters.states?.length ? filters.states : null,
          establishments: null, // Will be set below based on user profile
          generators: null, // Will be set below based on user profile
          granularity,
          metric
        };

        // 🔒 SECURITY FILTER: Apply filters based on user profile type
        if (isInternal) {
          // Internal users can use manual filters
          if (filters.generators && filters.generators.length > 0) {
            rpcParams.generators = filters.generators;
          }
          if (filters.establishments && filters.establishments.length > 0) {
            rpcParams.establishments = filters.establishments;
          }
        } else if (isStandard && profile.user_id) {
          // STANDARD users: filter by permitted establishments only
          const { data: permissionsData, error: permError } = await supabase
            .from('user_establishment_permissions')
            .select('establishment_id')
            .eq('user_id', profile.user_id);

          if (permError) {
            console.error('Error fetching user permissions:', permError);
            return [];
          }

          const permittedEstablishments = permissionsData?.map(p => p.establishment_id) || [];
          
          // If user has no permissions, return empty
          if (permittedEstablishments.length === 0) {
            return [];
          }

          // Determine which establishments to query
          let establishmentsToQuery: string[];
          
          if (filters.establishments && filters.establishments.length > 0) {
            // Intersection: only those selected AND permitted
            establishmentsToQuery = filters.establishments.filter(e => 
              permittedEstablishments.includes(e)
            );
            // If none of the selected establishments are permitted, return empty
            if (establishmentsToQuery.length === 0) {
              return [];
            }
          } else {
            // Use all permitted establishments
            establishmentsToQuery = permittedEstablishments;
          }

          rpcParams.establishments = establishmentsToQuery;
        } else if (isAdmin && profile.generator_id_legacy) {
          // ADMIN users: filter by generator (RPC expects UUID[])
          rpcParams.generators = [profile.generator_id_legacy];
          if (filters.establishments && filters.establishments.length > 0) {
            rpcParams.establishments = filters.establishments;
          }
        } else if (profile.generator_id_legacy) {
          // Fallback for other non-internal users: filter by generator
          rpcParams.generators = [profile.generator_id_legacy];
        }

        const { data, error } = await supabase.rpc('get_evolution_chart_data', rpcParams);

        if (error) {
          // Retorna array vazio em vez de lançar erro para não quebrar a UI
          return [];
        }

        if (!data || data.length === 0) {
          return [];
        }

        // Transform RPC data into chart format
        const groupedData: { [period: string]: { [material: string]: number } } = {};
        
        data.forEach((row: any) => {
          const materialLabel = getMaterialDisplayLabel(row.material_type);
          
          if (!groupedData[row.period]) {
            groupedData[row.period] = {};
          }
          
          groupedData[row.period][materialLabel] = Number(row.value) || 0;
        });

        // Convert to array format for Recharts
        const result: EvolutionDataPoint[] = Object.entries(groupedData).map(([period, materials]) => {
          const dataPoint: EvolutionDataPoint = { period };
          Object.entries(materials).forEach(([material, value]) => {
            dataPoint[material] = value;
          });
          return dataPoint;
        });
        
        return result;
      } catch (err) {
        return [];
      }
    },
    enabled: isValid && !!profile, // Só executa se os parâmetros forem válidos e o perfil estiver disponível
    retry: false, // Não tenta novamente automaticamente
    staleTime: 60 * 60 * 1000, // 1 hora
    gcTime: 24 * 60 * 60 * 1000, // 24 horas
  });
};
